// La paleta de un cliente se DERIVA de su manual de marca, con los mismos papeles que
// cumplen los colores de Atlas (ADR-2026-10-26-paleta-de-cliente-desde-el-manual):
//
// - `primary` es el color principal de la marca, el saturado (el azul de Atlas, el
//   naranja de WE ARE): va en ~4.000 lugares, así que es el que hace que la app "se vea"
//   de esa marca. Si no llega a 4.5:1 con texto blanco, se oscurece lo justo SIN cambiar
//   el tono (en OKLCH), en vez de reemplazarlo por otro color de la marca.
// - `deep` es el color secundario, el oscuro (el navy de Atlas, el azulado de WE ARE):
//   héroes, gradientes, chips con texto profundo.
// - `accent` es el principal tal cual lo dice el manual (o un tercer color si la marca lo
//   tiene), aclarado sólo si el texto oscuro no se lee encima: barras, badges, la mascota.
// - `primaryHover` y `accentSoft` salen de los anteriores con la misma distancia de luz
//   que tienen en Atlas, así el equilibrio de claros y oscuros es el mismo en cada cliente.
//
// Todo lo que sale de acá pasa `verificarContraste` por construcción (lo cubre el test).

import type { ColoresDeBranding } from './catalogo';
import { AA_TEXTO, BLANCO, HOVER_DISTINGUIBLE, ratioDeContraste, SUPERFICIE_GRIS } from './contraste';
import { esHex } from './color';

export interface ColoresDelManual {
  /** El color principal de la marca: el saturado, el que la identifica. */
  principal: string;
  /** El secundario, si el manual tiene uno oscuro. Sin él, `deep` es el principal oscurecido. */
  secundario?: string;
  /** Un tercer color para detalles. Sin él, el acento es el principal tal cual. */
  acento?: string;
}

// Luz (L de OKLCH) que tienen los tokens de Atlas: son la referencia del equilibrio.
/** Cuánto baja la luz del primario al hover (Atlas: #1644EB → #1238C8). */
const BAJADA_DE_HOVER = 0.057;
/** La luz del `deep` cuando no hay secundario (Atlas: #01269B). */
const LUZ_DEEP = 0.35;
/** La luz del acento suave (Atlas: #D6F8FB). */
const LUZ_ACENTO_SUAVE = 0.956;

type Oklch = [l: number, c: number, h: number];

const lineal = (v: number) => {
  const s = v / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};
const gamma = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);

function aOklch(hex: string): Oklch {
  if (!esHex(hex)) throw new Error(`No es un hex de seis dígitos: ${hex}`);
  const n = parseInt(hex.slice(1), 16);
  const r = lineal((n >> 16) & 255);
  const g = lineal((n >> 8) & 255);
  const b = lineal(n & 255);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return [L, Math.hypot(A, B), Math.atan2(B, A)];
}

/** RGB lineal; fuera de [0, 1] = el color no existe en pantalla. */
function aRgbLineal([L, C, H]: Oklch): [number, number, number] {
  const A = C * Math.cos(H);
  const B = C * Math.sin(H);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960695 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const enPantalla = (c: Oklch) => aRgbLineal(c).every((v) => v >= -1e-4 && v <= 1 + 1e-4);

/** A hex, bajando el croma hasta que el color exista (mismo tono y misma luz). */
function aHex([L, C, H]: Oklch): string {
  let croma = C;
  while (croma > 0 && !enPantalla([L, croma, H])) croma = Math.max(0, croma - 0.001);
  const canales = aRgbLineal([L, croma, H]).map((v) => Math.round(Math.min(1, Math.max(0, gamma(v))) * 255));
  return `#${canales.map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

/** Mueve la luz de a poco en `paso` hasta que `cumple`; el tono no cambia. */
function moverLuzHasta(hex: string, paso: number, cumple: (h: string) => boolean): string {
  if (cumple(hex)) return hex;
  const [L, C, H] = aOklch(hex);
  for (let luz = L + paso; luz > 0 && luz < 1; luz += paso) {
    const candidato = aHex([luz, C, H]);
    if (cumple(candidato)) return candidato;
  }
  return paso < 0 ? '#000000' : '#FFFFFF';
}

/** Lleva un color a la luz `luz` acercándolo al blanco en OKLab (un tinte del mismo tono). */
function tinte(hex: string, luz: number): string {
  const [L, C, H] = aOklch(hex);
  const t = L >= luz ? 0 : (luz - L) / (1 - L);
  return aHex([luz > L ? luz : L, C * (1 - t), H]);
}

/**
 * Los seis tokens de la app a partir de los colores del manual. `black` (el texto de toda
 * la app) no es de la marca: se pasa el de Atlas.
 */
export function paletaDesdeElManual(manual: ColoresDelManual, black: string): ColoresDeBranding {
  const legibleConBlanco = (h: string) =>
    ratioDeContraste(h, BLANCO) >= AA_TEXTO && ratioDeContraste(h, SUPERFICIE_GRIS) >= AA_TEXTO;

  const primary = moverLuzHasta(manual.principal, -0.002, legibleConBlanco);
  const [L, C, H] = aOklch(primary);
  let primaryHover = aHex([L - BAJADA_DE_HOVER, C, H]);
  if (ratioDeContraste(primary, primaryHover) < HOVER_DISTINGUIBLE) {
    primaryHover = moverLuzHasta(primaryHover, -0.002, (h) => ratioDeContraste(primary, h) >= HOVER_DISTINGUIBLE);
  }

  const deep = manual.secundario
    ? moverLuzHasta(manual.secundario, -0.002, (h) => ratioDeContraste(h, BLANCO) >= AA_TEXTO)
    : aHex([Math.min(LUZ_DEEP, L), C, H]);

  const accent = moverLuzHasta(manual.acento ?? manual.principal, 0.002, (h) => ratioDeContraste(black, h) >= AA_TEXTO);
  const accentSoft = moverLuzHasta(tinte(accent, LUZ_ACENTO_SUAVE), 0.002, (h) => ratioDeContraste(black, h) >= AA_TEXTO);

  return { primary, primaryHover, deep, black, accent, accentSoft };
}
